import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import {
  FEMALE_BASKET_GLB,
  FEMALE_CART_GLB,
  SELECTION_COLOR,
  SELECTION_OPACITY,
} from './constants';
import {
  FixtureItem,
  QueueLineColors,
  ShopperMesh,
  ShopperType,
} from './types';

const modelCache: Record<string, THREE.Group> = {};

const loadGLBModel = (url: string): Promise<THREE.Group> => {
  return new Promise((resolve, reject) => {
    if (modelCache[url]) {
      resolve(modelCache[url].clone());
      return;
    }

    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        modelCache[url] = gltf.scene;
        resolve(gltf.scene.clone());
      },
      undefined,
      (error) => {
        console.error('Error loading GLB model:', error);
        reject(error);
      }
    );
  });
};

export const isBoxWithinFloorBounds = (
  point: THREE.Vector3,
  boxWidth: number,
  boxHeight: number,
  floorPlane: THREE.Mesh
): boolean => {
  const floorBounds = new THREE.Box3().setFromObject(floorPlane);
  const halfWidth = boxWidth / 2;
  const halfDepth = boxHeight / 2; // For 3D objects, height (in parameter) becomes depth (z-axis)

  return (
    point.x - halfWidth >= floorBounds.min.x &&
    point.x + halfWidth <= floorBounds.max.x &&
    point.z - halfDepth >= floorBounds.min.z &&
    point.z + halfDepth <= floorBounds.max.z
  );
};

/**
 * Add a red dot visualization to the center of a mesh
 */
const addCenterVisualization = (object: THREE.Mesh): void => {
  // Ensure we have up-to-date world matrices so boundingBox is correct
  object.updateWorldMatrix(true, false);

  const boundingBox = new THREE.Box3().setFromObject(object);
  const centerWorld = new THREE.Vector3();
  boundingBox.getCenter(centerWorld);

  // Create a sprite so it always faces the camera and ignores depth testing
  const spriteMaterial = new THREE.SpriteMaterial({
    color: 0xff0000,
    depthTest: false, // Always render on top of the object itself
    depthWrite: false,
  });

  const redDot = new THREE.Sprite(spriteMaterial);
  redDot.scale.set(0.1, 0.1, 0.1); // Size of the red dot (adjust as needed)

  // Position the sprite at the center of the object
  const offsetWorld = centerWorld.clone();

  // Convert world position to the object's local space so the sprite moves with the mesh
  object.worldToLocal(offsetWorld);
  redDot.position.copy(offsetWorld);

  redDot.renderOrder = 999; // Ensure it renders last
  redDot.userData = { isCenterPointVisualization: true };

  object.add(redDot);
};

export const createFixtureMesh = (
  item: FixtureItem,
  position: THREE.Vector3,
  renderType: 'rendered' | 'lined' = 'rendered'
): THREE.Mesh => {
  let renderPath = '';

  if (item.figure?.renders) {
    if (renderType === 'rendered') {
      renderPath = item.figure.renders.topdown_l || '';
    } else {
      renderPath = item.figure.renders.front || '';
    }
  }

  // Create placeholder mesh that will hold the 3D model
  const geometry = new THREE.PlaneGeometry(item.size.width, item.size.height);
  const material = new THREE.MeshBasicMaterial({
    color: item.color || 0xcccccc,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
  });

  // Create mesh with placeholder material
  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2; // Rotate to lie flat on the floor
  mesh.position.copy(position);
  mesh.position.y = 0.02; // Slightly above the floor

  // Store fixture data
  mesh.userData = {
    isInteractive: true,
    draggable: true,
    fixtureId: item.id,
    type: 'fixture',
    name: item.name,
    category: item.category,
    size: item.size,
    renderType,
    is3DModel: true,
    imageUrl: item.imageUrl,
  };

  // Add red dot visualization immediately after creating the mesh
  addCenterVisualization(mesh);

  // Load the GLB/GLTF model
  const loader = new GLTFLoader();

  loader.load(
    renderPath,
    (gltf) => {
      console.log(`Successfully loaded 3D model: ${renderPath}`, gltf);

      // Check if model has materials
      if (gltf.scene) {
        gltf.scene.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            console.log(`Model child mesh:`, {
              name: mesh.name,
              geometry: mesh.geometry ? 'Available' : 'Missing',
              material: mesh.material ? 'Available' : 'Missing',
              hasMaterial: !!mesh.material,
              materialType: mesh.material
                ? Array.isArray(mesh.material)
                  ? 'Array of materials'
                  : (mesh.material as THREE.Material).type
                : 'None',
              materialMap:
                mesh.material &&
                !Array.isArray(mesh.material) &&
                (mesh.material as THREE.MeshStandardMaterial).map
                  ? 'Has texture map'
                  : 'No texture map',
            });
          }
        });
      }

      // Remove the placeholder plane
      while (mesh.children.length > 0) {
        mesh.remove(mesh.children[0]);
      }

      // Add the loaded model to our mesh
      const model = gltf.scene;
      if (renderPath.includes('walmart-texture.glb')) {
        // Make sure textures are preserved for Walmart models
        model.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            const meshChild = child as THREE.Mesh;
            if (meshChild.material) {
              if (Array.isArray(meshChild.material)) {
                meshChild.material.forEach((mat) => {
                  if (mat instanceof THREE.MeshStandardMaterial) {
                    mat.needsUpdate = true;
                    mat.transparent = true;
                    mat.side = THREE.DoubleSide;
                    mat.roughness = 0.5;
                    mat.metalness = 0.1;
                    mat.envMapIntensity = 1.0;
                    if (mat.map) {
                      mat.map.needsUpdate = true;
                    }
                  }
                });
              } else {
                const standardMat = meshChild.material as
                  | THREE.MeshStandardMaterial
                  | THREE.MeshBasicMaterial;
                if (standardMat.map) {
                  standardMat.needsUpdate = true;
                  standardMat.transparent = true;
                  standardMat.side = THREE.DoubleSide;
                  // Additional properties for better texture rendering
                  if (standardMat instanceof THREE.MeshStandardMaterial) {
                    standardMat.roughness = 0.5;
                    standardMat.metalness = 0.1;
                    standardMat.envMapIntensity = 1.0;
                  }
                  // Ensure texture is properly applied
                  if (standardMat.map) {
                    standardMat.map.needsUpdate = true;
                  }
                }
              }
            }
          }
        });

        // Scale model to fit within the intended size
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        box.getSize(size);

        const scaleX = item.size.width / size.x;
        const scaleZ = item.size.height / size.z;
        const scale = Math.min(scaleX, scaleZ);

        model.scale.set(scale, scale, scale);
      } else {
        // Scale model to fit within the intended size
        const box = new THREE.Box3().setFromObject(model);
        const size = new THREE.Vector3();
        box.getSize(size);

        const scaleX = item.size.width / size.x;
        const scaleZ = item.size.height / size.z;
        const scale = Math.min(scaleX, scaleZ);

        model.scale.set(scale, scale, scale);
      }

      // Rotate model to face correct direction
      model.rotation.x = Math.PI / 2;
      mesh.add(model);

      // Re-add the center visualization after the model is loaded and added
      // First, remove any existing center visualization
      const existingDot = mesh.children.find(
        (child) => child.userData.isCenterPointVisualization
      );
      if (existingDot) {
        mesh.remove(existingDot);
      }

      // Add the visualization again now that the model is loaded
      addCenterVisualization(mesh);
    },
    (progress) => {
      const percentComplete = (progress.loaded / progress.total) * 100;
      console.log(`Loading progress: ${percentComplete.toFixed(2)}%`);
    },
    (error) => {
      console.error(`Error loading 3D model from ${renderPath}:`, error);
    }
  );

  return mesh;
};

// Get shopper selection overlay dimensions based on shopper type
const getShopperOverlayDimensions = (
  shopperType: ShopperType
): { width: number; height: number; depth: number } => {
  switch (shopperType) {
    case ShopperType.WomanWithBasket:
      return {
        width: 0.68,
        height: 0.43,
        depth: 0.1,
      };
    case ShopperType.WomanWithCart:
      return {
        width: 0.51,
        height: 1,
        depth: 0.1,
      };
    default:
      return {
        width: 0.35,
        height: 0.5,
        depth: 0.1,
      };
  }
};

export const addSelectionOverlay = (
  mesh: THREE.Mesh,
  scene: THREE.Scene,
  color: string | number = SELECTION_COLOR,
  opacity: number = SELECTION_OPACITY
): void => {
  removeSelectionOverlay(mesh, scene);

  const orphanedOverlays = scene.children.filter(
    (obj) =>
      obj.userData.isSelectionOverlay &&
      obj.position.distanceTo(mesh.position) < 0.1
  );

  orphanedOverlays.forEach((overlay) => scene.remove(overlay));

  let overlayGeometry;

  if (mesh.userData.isCube) {
    // Create a slightly larger cube for selection
    const width = mesh.userData.width * 1.05;
    const height = mesh.userData.height * 1.05;
    const depth = mesh.userData.depth * 1.05;

    overlayGeometry = new THREE.BoxGeometry(width, height, depth);
  } else if (mesh.userData.isSphere) {
    // Create a slightly larger sphere for selection
    const radius = mesh.userData.radius * 1.05;

    overlayGeometry = new THREE.SphereGeometry(radius, 32, 32);
  } else if (mesh.userData.isQueueLineShopper) {
    // Get custom overlay dimensions for this shopper type
    const dimensions = getShopperOverlayDimensions(mesh.userData.type);

    overlayGeometry = new THREE.BoxGeometry(
      dimensions.width,
      dimensions.depth,
      dimensions.height
    );
  } else if (mesh.geometry) {
    overlayGeometry = mesh.geometry.clone();
  } else {
    overlayGeometry = new THREE.BoxGeometry(0.5, 0.1, 0.5);
  }

  const overlayMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);

  overlay.position.copy(mesh.position);
  overlay.rotation.copy(mesh.rotation);

  overlay.position.y += 0.001;

  overlay.userData.isOverlay = true;
  overlay.userData.isSelectionOverlay = true;
  overlay.userData.forObject = mesh.uuid;

  scene.add(overlay);

  mesh.userData.hasOverlay = overlay.uuid;
};

export const removeSelectionOverlay = (
  mesh: THREE.Mesh,
  scene: THREE.Scene
): void => {
  if (mesh.userData.hasOverlay) {
    const overlayUuid = mesh.userData.hasOverlay;
    const overlay = scene.children.find((obj) => obj.uuid === overlayUuid);
    if (overlay) {
      scene.remove(overlay);
    }
    delete mesh.userData.hasOverlay;
  }
};

export const createCubeMesh = (position: THREE.Vector3) => {
  // Create a cube mesh with dimension 0.2
  const geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
  const color = '#ff0000';
  const material = new THREE.MeshBasicMaterial({
    color: color,
  });
  const cube = new THREE.Mesh(geometry, material);

  // Position the cube
  cube.position.copy(position);
  cube.position.y = 0.1; // Elevate it above the floor

  // Add metadata for interaction
  cube.userData = {
    isInteractive: true,
    isCube: true,
    width: 0.2,
    height: 0.2,
    depth: 0.2,
    originalColor: color,
  };

  return cube;
};

export const createSphereMesh = (position: THREE.Vector3) => {
  // Create a sphere mesh with radius 0.1
  const geometry = new THREE.SphereGeometry(0.1, 32, 32);
  const color = '#00ff00';
  const material = new THREE.MeshBasicMaterial({
    color: color,
  });
  const sphere = new THREE.Mesh(geometry, material);

  // Position the sphere
  sphere.position.copy(position);
  sphere.position.y = 0.1; // Elevate it above the floor

  // Add metadata for interaction
  sphere.userData = {
    isInteractive: true,
    isSphere: true,
    radius: 0.1,
    originalColor: color,
  };

  return sphere;
};

// Calculate scale for 3D models based on shopper type
const calculateModelScale = (
  model: THREE.Group,
  shopperType: ShopperType
): number => {
  // Get the dimensions for this shopper type
  const dimensions = getShopperDimensions(shopperType);

  // Calculate the bounding box of the model
  const box = new THREE.Box3().setFromObject(model);
  const size = new THREE.Vector3();
  box.getSize(size);

  // Calculate scale factors based on width and height
  const scaleX = dimensions.width / size.x;
  const scaleZ = dimensions.height / size.z;

  // Use the smaller scale to ensure the model fits within the dimensions
  let scale = Math.min(scaleX, scaleZ);

  // Apply specific scaling for different shopper types
  if (shopperType === ShopperType.WomanWithBasket) {
    scale *= 0.75;
  } else if (shopperType === ShopperType.WomanWithCart) {
    scale *= 0.6;
  }

  return scale;
};

// Queue Line Functions

export const createShopperMesh = (
  position: THREE.Vector3,
  shopperType: ShopperType,
  orderInLine: number,
  lineId: number,
  color: QueueLineColors,
  rotation = 0,
  scene: THREE.Scene
): ShopperMesh => {
  // Define dimensions based on shopper type
  const dimensions = getShopperDimensions(shopperType);

  // Create a container for the shopper
  const boundingGeometry = new THREE.BoxGeometry(
    dimensions.width,
    0.1,
    dimensions.height
  );
  const boundingMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0.01,
    depthWrite: false,
  });
  const shopperContainer = new THREE.Mesh(boundingGeometry, boundingMaterial);
  shopperContainer.position.copy(position);
  shopperContainer.position.y = 0.02;

  // Add userData to the container
  const shopperRadius = dimensions.width / 2;
  shopperContainer.userData = {
    isInteractive: true,
    isQueueLineShopper: true,
    orderInLine: orderInLine,
    lineId: lineId,
    color: color,
    type: shopperType,
    elapsedWaitTime: '00:30',
    geometry: boundingGeometry,
    isBoundingBox: true,
    width: dimensions.width,
    height: dimensions.height,
    depth: 0.1, // From boundingGeometry
    radius: shopperRadius,
    collisionBox: new THREE.Box3(
      new THREE.Vector3(-dimensions.width / 2, -0.05, -dimensions.height / 2),
      new THREE.Vector3(dimensions.width / 2, 0.05, dimensions.height / 2)
    ),
  };

  // Create a container for the badge that won't rotate with the shopper
  const badgeContainer = new THREE.Object3D();
  badgeContainer.position.copy(position);
  badgeContainer.position.y = 0.025;
  badgeContainer.rotation.x = -Math.PI / 2;

  // Add the badge container to shopperContainer userData
  shopperContainer.userData.badgeContainer = badgeContainer;

  // Add the containers to the scene
  scene.add(shopperContainer);
  scene.add(badgeContainer);

  // Add order badge to the container
  const badgeMesh = createOrderBadge(orderInLine, color, false);
  badgeMesh.position.set(0, 0.005, 0);
  badgeContainer.add(badgeMesh);

  // Load the 3D model
  const modelUrl =
    shopperType === ShopperType.WomanWithBasket
      ? FEMALE_BASKET_GLB
      : FEMALE_CART_GLB;

  // Load the 3D model
  loadGLBModel(modelUrl)
    .then((model) => {
      // Add model to container with appropriate scale
      const modelScale = calculateModelScale(model, shopperType);
      model.scale.set(modelScale, modelScale, modelScale);

      // Rotate 180 degrees around Y axis to face the correct direction
      model.rotation.y = Math.PI;

      // Apply any additional rotation for facing direction
      if (rotation) {
        model.rotation.y += rotation;
      }

      // Center the model horizontally while keeping it on the floor
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      model.position.x -= center.x;
      model.position.z -= center.z;

      console.log(`Added shopper model with scale ${modelScale}: ${modelUrl}`);

      // Add model to the shopper container
      shopperContainer.add(model);

      // Make child meshes non-interactive
      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          child.userData = {
            isInteractive: false, // Explicitly make child non-interactive
            parentContainer: shopperContainer,
          };
        }
      });

      // Ensure container is interactive and has all necessary properties
      shopperContainer.userData = {
        ...shopperContainer.userData,
        isInteractive: true,
        isQueueLineShopper: true,
        width: dimensions.width,
        height: dimensions.height,
        depth: 0.1,
      };
    })
    .catch((error) => {
      console.error('Failed to load 3D model:', error);
    });

  return shopperContainer as unknown as ShopperMesh;
};

export const createOrderBadge = (
  orderNumber: number,
  color: string,
  isSelected = false
): THREE.Mesh => {
  const container = new THREE.Object3D();

  // Create a circle for the border (use the shopper's queue line color)
  const borderGeometry = new THREE.CircleGeometry(0.115, 32);
  const borderMaterial = new THREE.MeshBasicMaterial({
    color: color, // Use the shopper's queue line color
    side: THREE.DoubleSide,
  });
  const border = new THREE.Mesh(borderGeometry, borderMaterial);
  container.add(border);

  // Create a circle for the badge background (white or queue line color when selected)
  const badgeGeometry = new THREE.CircleGeometry(0.1, 32);
  const badgeMaterial = new THREE.MeshBasicMaterial({
    color: isSelected ? color : '#FFFFFF',
    side: THREE.DoubleSide,
  });
  const badge = new THREE.Mesh(badgeGeometry, badgeMaterial);
  badge.position.z = 0.001; // Slightly in front of the border
  container.add(badge);

  // Create text texture for the order number
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d');

  if (context) {
    context.fillStyle = isSelected ? '#FFFFFF' : color;
    context.font = 'bold 40px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(orderNumber.toString(), 32, 32);

    const texture = new THREE.CanvasTexture(canvas);

    // Create a smaller circle for the number
    const numberGeometry = new THREE.CircleGeometry(0.09, 32);
    const numberMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
    });

    const numberMesh = new THREE.Mesh(numberGeometry, numberMaterial);
    numberMesh.position.z = 0.002; // Slightly in front of the white background
    container.add(numberMesh);
  }

  return container as THREE.Mesh;
};

export const updateShopperOrderBadge = (
  shopperMesh: ShopperMesh,
  newOrderInLine: number,
  isSelected = false
): void => {
  // Update the userData
  shopperMesh.userData.orderInLine = newOrderInLine;

  // Update the badge in the badge container
  if (shopperMesh.userData.badgeContainer) {
    const badgeContainer = shopperMesh.userData
      .badgeContainer as THREE.Object3D;

    // Clear existing children from badge container
    while (badgeContainer.children.length > 0) {
      badgeContainer.remove(badgeContainer.children[0]);
    }

    // Create a new badge with the updated order number
    const newBadge = createOrderBadge(
      newOrderInLine,
      shopperMesh.userData.color,
      isSelected
    );
    newBadge.position.set(0, 0.005, 0);
    badgeContainer.add(newBadge);
  }
};

export const getShopperDimensions = (
  shopperType: ShopperType
): { width: number; height: number } => {
  // Convert inches to meters (1 inch = 0.0254 meters)
  const INCHES_TO_METERS = 0.0254;

  switch (shopperType) {
    case ShopperType.WomanWithBasket:
      return {
        width: 26.794 * INCHES_TO_METERS,
        height: 13.6 * INCHES_TO_METERS,
      };
    case ShopperType.WomanWithCart:
      return {
        width: 20.13 * INCHES_TO_METERS,
        height: 53.57 * INCHES_TO_METERS,
      };
    default:
      return {
        width: 0.35,
        height: 0.5,
      };
  }
};

export const isShopperOverlapping = (
  shopperMesh: ShopperMesh,
  otherMeshes: THREE.Mesh[]
): boolean => {
  // Use custom collision box if available, otherwise create new bounding box
  const shopperBox = shopperMesh.userData.collisionBox
    ? (shopperMesh.userData.collisionBox as THREE.Box3)
        .clone()
        .translate(shopperMesh.position)
    : new THREE.Box3().setFromObject(shopperMesh);

  // Check against all other meshes
  for (const otherMesh of otherMeshes) {
    // Skip the same mesh or non-interactive meshes
    if (
      otherMesh === shopperMesh ||
      !otherMesh.userData.isInteractive ||
      otherMesh.userData.isOverlay
    ) {
      continue;
    }

    // Use custom collision box for other mesh if available
    const otherBox = otherMesh.userData.collisionBox
      ? (otherMesh.userData.collisionBox as THREE.Box3)
          .clone()
          .translate(otherMesh.position)
      : new THREE.Box3().setFromObject(otherMesh);

    if (shopperBox.intersectsBox(otherBox)) {
      return true;
    }
  }

  return false;
};

export const updateShopperBadgeSelection = (
  shopperMesh: ShopperMesh,
  isSelected: boolean
): void => {
  if (
    shopperMesh &&
    shopperMesh.userData &&
    shopperMesh.userData.isQueueLineShopper
  ) {
    updateShopperOrderBadge(
      shopperMesh,
      shopperMesh.userData.orderInLine,
      isSelected
    );
  }
};
